import { useEffect, useState } from "react";
import { useHeadingRef } from "@/lib/barTitle";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ListMusic, Loader2, Play, Plus, Shuffle, Trash2, Pencil, Sparkles, CalendarHeart, Download, FileArchive, FileAudio, Share2, Users, CopyPlus, SlidersHorizontal, RefreshCw } from "lucide-react";
import { DynamicPlaylistDialog } from "@/components/SmartPlaylistDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { PinButton } from "@/components/PinButton";
import { findPin } from "@/lib/offline";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Cover } from "@/components/Cover";
import { TrackTable } from "@/components/TrackTable";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { usePlayer } from "@/context/PlayerContext";
import { usePlaylists } from "@/context/PlaylistsContext";
import { useAuth } from "@/context/AuthContext";
import { ShareDialog } from "@/components/ShareDialog";
import { SharedWithYou } from "@/pages/SharedPlaylist";
import { api, downloadFile, fmtTime, formatApiError } from "@/lib/api";
import { useCrumb } from "@/lib/trail";

const PLAYLISTS_PARENT = { label: "Playlists", path: "/playlists" };

export const startDownload = async (path, msg) => {
  try { await downloadFile(path); toast.success(msg, { description: "Large playlists can take a while to finish." }); }
  catch (e) { toast.error(formatApiError(e)); }
};

export const Mosaic = ({ ids, className = "" }) => {
  if (ids.length < 4) return <Cover albumId={ids[0]} hasCover={!!ids[0]} className={`aspect-square w-full ${className}`} iconSize={36} />;
  return (
    <div className={`grid aspect-square w-full grid-cols-2 overflow-hidden rounded-md ${className}`}>
      {ids.slice(0, 4).map((id) => <Cover key={id} albumId={id} rounded="rounded-none" className="aspect-square h-full w-full" iconSize={18} />)}
    </div>
  );
};

const EditDialog = ({ open, onOpenChange, initial, onSave, title }) => {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  useEffect(() => { if (open) { setName(initial?.name || ""); setDescription(initial?.description || ""); } }, [open, initial]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="playlist-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Playlist name" data-testid="playlist-name-input" />
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" data-testid="playlist-description-input" />
        </div>
        <DialogFooter>
          <button disabled={!name.trim()} onClick={() => onSave({ name: name.trim(), description })} data-testid="playlist-save-button"
            className="rounded-full bg-zinc-900 px-6 py-2 text-sm text-white disabled:opacity-40">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SOURCE_LABEL = { builder: " · AI mix", smart: " · Weekly mix", rules: " · Dynamic · rules", ai: " · Dynamic · AI", artist: " · Artist" };

const SmartMixCard = ({ onCreated }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { api.get("/smart-mix").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/smart-mix/generate");
      toast.success(`${data.name} is ready`);
      onCreated();
      navigate(`/playlists/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  const toggle = async (enabled) => { const { data } = await api.put("/smart-mix", { enabled }); setS(data); };
  return (
    <div className="mb-12 flex flex-col gap-6 rounded-lg border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:flex-row sm:items-center" data-testid="smart-mix-card">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-zinc-900 text-amber-400"><CalendarHeart size={20} /></div>
      <div className="min-w-0 flex-1">
        <p className="font-heading text-xl font-bold tracking-tight">Weekly Mix</p>
        <p className="text-sm text-zinc-600">A fresh AI playlist every Monday, built from your most-played artists and genres of the last 30 days. The last 4 are kept.</p>
        {s.latest && <Link to={`/playlists/${s.latest.id}`} className="mt-1 inline-block text-xs text-amber-800 underline-offset-4 hover:underline" data-testid="smart-mix-latest-link">Latest: {s.latest.name} · {s.latest.track_count} song{s.latest.track_count === 1 ? "" : "s"}</Link>}
      </div>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-xs text-zinc-600">
          <Switch checked={s.enabled} onCheckedChange={toggle} data-testid="smart-mix-switch" />Every Monday
        </label>
        <button onClick={generate} disabled={busy} data-testid="smart-mix-generate-button"
          className="flex h-10 items-center gap-2 rounded-full bg-zinc-900 px-5 text-sm text-white transition-transform hover:scale-[1.03] disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} className="text-amber-400" />}{busy ? "Mixing…" : "Make one now"}
        </button>
      </div>
    </div>
  );
};

export function Playlists() {
  const { playlists, create, refresh } = usePlaylists();
  const [open, setOpen] = useState(false);
  const [smartOpen, setSmartOpen] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { refresh(); }, [refresh]);
  const save = async ({ name, description }) => {
    const p = await create(name, [], { description });
    setOpen(false);
    navigate(`/playlists/${p.id}`);
  };
  return (
    <div>
      <PageHeader eyebrow={`${playlists.length} playlists`} title="Playlists" testid="playlists-title">
        <button onClick={() => setSmartOpen(true)} data-testid="playlist-create-smart-button" className="flex h-10 items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-5 text-sm text-amber-900 hover:border-amber-700">
          <Sparkles size={14} className="text-amber-600" /> Dynamic playlist
        </button>
        <button onClick={() => setOpen(true)} data-testid="playlist-create-button" className="flex h-10 items-center gap-2 rounded-full bg-zinc-900 px-5 text-sm text-white hover:bg-zinc-800">
          <Plus size={15} /> New playlist
        </button>
      </PageHeader>
      <DynamicPlaylistDialog open={smartOpen} onOpenChange={setSmartOpen} onSaved={(pl) => { refresh(); navigate(`/playlists/${pl.id}`); }} />
      <SmartMixCard onCreated={refresh} />
      {playlists.length === 0 ? (
        <EmptyState icon={ListMusic} title="No playlists yet" text="Create one by hand, or let the Playlist Builder mix one from your favourite artists and genres." />
      ) : (
        <div className="grid grid-cols-2 gap-x-5 gap-y-8 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {playlists.map((p, i) => (
            <Link key={p.id} to={`/playlists/${p.id}`} data-testid={`playlist-card-${p.id}`} className="group animate-rise" style={{ animationDelay: `${i * 30}ms` }}>
              <div className="overflow-hidden rounded-md border border-zinc-200 transition-transform duration-300 group-hover:-translate-y-1">
                <Mosaic ids={p.cover_album_ids} />
              </div>
              <p className="mt-3 truncate text-sm font-medium">{p.name}</p>
              <p className="flex items-center gap-1.5 font-mono text-[11px] text-zinc-500">{p.track_count} song{p.track_count === 1 ? "" : "s"}{SOURCE_LABEL[p.source] || ""}
                {p.shared && <span className="flex items-center gap-0.5 rounded bg-sky-50 px-1 text-sky-700" data-testid={`playlist-shared-badge-${p.id}`}><Users size={10} />Shared</span>}</p>
            </Link>
          ))}
        </div>
      )}
      <SharedWithYou />
      <EditDialog open={open} onOpenChange={setOpen} onSave={save} title="New playlist" />
    </div>
  );
}

export function PlaylistDetail() {
  const { id } = useParams();
  const [pl, setPl] = useState(null);
  const [editing, setEditing] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [regen, setRegen] = useState(false);
  const [sharing, setSharing] = useState(false);
  useCrumb(pl?.name, PLAYLISTS_PARENT);
  const { user } = useAuth();
  const { refresh, create } = usePlaylists();
  const p = usePlayer();
  const navigate = useNavigate();
  useEffect(() => {
    setPl(null);
    api.get(`/playlists/${id}`).then((r) => setPl(r.data)).catch(() => {
      const p = findPin("playlist", id);
      setPl(p ? { id, name: p.name, description: "", tracks: p.tracks, track_ids: p.tracks.map((t) => t.id), cover_album_ids: p.coverAlbumIds, offline: true } : false);
    });
  }, [id]);
  const headingRef = useHeadingRef(pl ? pl.name : "", pl ? `Playlist · ${pl.tracks.length} ${pl.tracks.length === 1 ? "song" : "songs"}` : "",
    pl ? { play: () => p.playTracks(pl.tracks), shuffle: () => p.playTracks([...pl.tracks].sort(() => Math.random() - 0.5)), disabled: !pl.tracks.length } : null);
  if (pl === false) return <EmptyState icon={ListMusic} title="Playlist not found" text="It may have been deleted." />;
  if (!pl) return <Loader2 className="animate-spin text-zinc-400" />;

  const put = async (patch) => {
    try {
      const { data } = await api.put(`/playlists/${id}`, patch);
      setPl(data);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = (track) => {
    const idx = pl.track_ids.indexOf(track.id);
    put({ track_ids: pl.track_ids.filter((_, i) => i !== idx) });
  };
  const reorder = (from, to, next) => put({ track_ids: next.map((t) => t.id) });
  const duplicate = async () => {
    try {
      const copy = await create(`${pl.name} (copy)`, pl.track_ids, { description: pl.description || "" });
      toast.success(`Created "${copy.name}"`);
      navigate(`/playlists/${copy.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Delete playlist "${pl.name}"?`)) return;
    await api.delete(`/playlists/${id}`);
    refresh();
    navigate("/playlists");
  };
  const total = pl.tracks.reduce((s, t) => s + (t.duration || 0), 0);
  const isSmart = pl.source === "rules";
  const isAi = pl.source === "ai" && !pl.offline;
  const toggleAuto = async (v) => {
    try {
      const { data } = await api.put(`/playlists/${pl.id}`, { auto_refresh: v });
      setPl(data);
      toast.success(v ? "Refreshes itself every Monday" : "Weekly refresh off — refresh manually any time");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const regenerate = async () => {
    setRegen(true);
    try {
      const { data } = await api.post(`/playlists/${pl.id}/regenerate`);
      setPl(data); refresh();
      toast.success(`Refreshed "${data.name}"`, { description: `${data.tracks.length} songs picked by AI from the same recipe.` });
    } catch (e) { toast.error(formatApiError(e)); }
    setRegen(false);
  };

  return (
    <div className="animate-rise">
      <div className="flex flex-col gap-10 md:flex-row md:items-end">
        <div className="w-full max-w-xs shrink-0 overflow-hidden rounded-md shadow-[0_20px_50px_-20px_rgba(0,0,0,0.35)] md:w-64"><Mosaic ids={pl.cover_album_ids} /></div>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-700">Playlist{SOURCE_LABEL[pl.source] || ""}</p>
          <h1 ref={headingRef} className="mt-3 font-heading text-4xl font-bold tracking-tight sm:text-5xl" data-testid="playlist-title">{pl.name}</h1>
          {pl.description && <p className="mt-3 max-w-xl text-sm text-zinc-600" data-testid="playlist-description">{pl.description}</p>}
          {isSmart && (
            <p className="mt-3 inline-flex max-w-xl items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs text-amber-800" data-testid="playlist-rules-text">
              <RefreshCw size={11} />Updates itself · {pl.rules_text}
            </p>
          )}
          <p className="mt-2 font-mono text-xs text-zinc-500">{pl.tracks.length} song{pl.tracks.length === 1 ? "" : "s"} · {fmtTime(total)}</p>
          <div className="mt-8 flex items-center gap-3">
            <button onClick={() => p.playTracks(pl.tracks)} disabled={!pl.tracks.length} data-testid="playlist-play-button"
              className="flex h-12 items-center gap-2 rounded-full bg-zinc-900 px-6 text-sm font-medium text-white transition-transform hover:scale-[1.03] disabled:opacity-40">
              <Play size={16} fill="currentColor" /> Play
            </button>
            <button onClick={() => p.playTracks([...pl.tracks].sort(() => Math.random() - 0.5))} disabled={!pl.tracks.length} data-testid="playlist-shuffle-button"
              className="flex h-12 items-center gap-2 rounded-full border border-zinc-300 bg-white px-5 text-sm hover:border-zinc-900 disabled:opacity-40">
              <Shuffle size={15} /> Shuffle
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button disabled={!pl.tracks.length} data-testid="playlist-download-menu" title="Download or export"
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-zinc-300 bg-white text-zinc-600 hover:border-zinc-900 disabled:opacity-40"><Download size={15} /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuItem onClick={() => startDownload(`/playlists/${pl.id}/download`, "ZIP download started")} data-testid="playlist-download-zip">
                  <FileArchive size={14} className="mr-2" />Download songs (ZIP)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => startDownload(`/playlists/${pl.id}/export.m3u8`, "M3U playlist exported")} data-testid="playlist-export-m3u">
                  <FileAudio size={14} className="mr-2" />Export M3U (stream links)
                </DropdownMenuItem>
                <p className="px-2 pb-1.5 pt-1 text-[11px] leading-snug text-zinc-500">M3U links stream from Resonance and work for 30 days in VLC and other players. The ZIP also includes an offline M3U.</p>
              </DropdownMenuContent>
            </DropdownMenu>
            {user?.role === "owner" && (
              <button onClick={() => setSharing(true)} data-testid="playlist-share-button" title="Share with guests"
                className={`flex h-12 items-center gap-2 rounded-full border px-4 text-sm ${pl.shared ? "border-sky-300 bg-sky-50 text-sky-800" : "border-zinc-300 bg-white text-zinc-600 hover:border-zinc-900"}`}>
                <Share2 size={15} />{pl.shared ? "Shared" : "Share"}
              </button>
            )}
            <PinButton type="playlist" id={pl.id} name={pl.name} subtitle={pl.description || ""} tracks={pl.tracks} coverAlbumIds={pl.cover_album_ids || []} testid="playlist-pin-offline" />
            {isAi && <button onClick={regenerate} disabled={regen} data-testid="playlist-regenerate-button" className="flex h-12 items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-4 text-sm text-amber-800 hover:bg-amber-100 disabled:opacity-50"><RefreshCw size={14} className={regen ? "animate-spin" : ""} />{regen ? "Refreshing…" : "Refresh with AI"}</button>}
            {isSmart && <button onClick={() => setRulesOpen(true)} data-testid="playlist-edit-rules-button" className="flex h-12 items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-4 text-sm text-amber-800 hover:bg-amber-100"><SlidersHorizontal size={14} />Edit rules</button>}
            <button onClick={() => setEditing(true)} data-testid="playlist-edit-button" title="Edit name and description" className="flex h-12 w-12 items-center justify-center rounded-full border border-zinc-300 bg-white text-zinc-600 hover:border-zinc-900"><Pencil size={15} /></button>
            {!pl.offline && <button onClick={duplicate} data-testid="playlist-duplicate-button" title="Duplicate playlist" className="flex h-12 w-12 items-center justify-center rounded-full border border-zinc-300 bg-white text-zinc-600 hover:border-zinc-900"><CopyPlus size={15} /></button>}
            <button onClick={del} data-testid="playlist-delete-button" className="flex h-12 w-12 items-center justify-center rounded-full border border-zinc-300 bg-white text-zinc-600 hover:border-red-500 hover:text-red-600"><Trash2 size={15} /></button>
          </div>
        </div>
      </div>
      <div className="mt-14">
        {isAi && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50/60 px-4 py-3 text-sm" data-testid="ai-auto-refresh-row">
            <span className="flex items-center gap-2 text-amber-900"><CalendarHeart size={14} />Refresh automatically every Monday<span className="text-xs text-amber-800/70">{pl.last_generated_at ? ` · last take ${new Date(pl.last_generated_at).toLocaleDateString()}` : ""}</span></span>
            <Switch checked={pl.auto_refresh !== false} onCheckedChange={toggleAuto} data-testid="ai-auto-refresh-switch" />
          </div>
        )}
        {pl.tracks.length ? <TrackTable tracks={pl.tracks} showCover onRemove={isSmart ? undefined : remove} onReorder={pl.offline || isSmart ? null : reorder} /> :
          <EmptyState icon={ListMusic} title={isSmart ? "No songs match these rules yet" : "This playlist is empty"} text={isSmart ? "Loosen the rules, or let the list fill up as you listen." : "Use the … menu on any song or album to add it here."} />}
      </div>
      {user?.role === "owner" && <ShareDialog open={sharing} onOpenChange={setSharing} playlist={pl} onSaved={(shared) => { setPl({ ...pl, shared }); refresh(); }} />}
      <EditDialog open={editing} onOpenChange={setEditing} initial={pl} title="Edit playlist" onSave={async (v) => { await put(v); setEditing(false); }} />
      {isSmart && <DynamicPlaylistDialog open={rulesOpen} onOpenChange={setRulesOpen} initial={pl} onSaved={(data) => { setPl(data); refresh(); }} />}
    </div>
  );
}
